import React from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatCurrency, convertFromMXN, convertToMXN } from '../../utils/formatters';
import { DollarSign, Target, Calendar, Percent, ChevronRight, Plus, Minus, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';

import { getLanguageContent, getLanguageFromCurrency } from '../../data/languages';

interface LongTermCentralDataProps {
  onNext: () => void;
}

const LongTermCentralData: React.FC<LongTermCentralDataProps> = ({ onNext }) => {
  const { investment, updateInvestment } = useCalculator();

  // Handlers for increment/decrement buttons
  const handleIncrementCertificates = () => {
    if (investment.initialCertificates < 20) {
      updateInvestment({ initialCertificates: investment.initialCertificates + 1 });
    }
  };

  const handleDecrementCertificates = () => {
    if (investment.initialCertificates > 1) {
      updateInvestment({ initialCertificates: investment.initialCertificates - 1 });
    }
  };

  const handleIncrementPrice = () => {
    const increment = convertToMXN(500, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
    const maxPrice = convertToMXN(300000, 'MXN', investment.exchangeRate, investment.exchangeRateEUR);
    
    if (investment.certificateBasePrice < maxPrice) {
      updateInvestment({ certificateBasePrice: investment.certificateBasePrice + increment });
    }
  };

  const handleDecrementPrice = () => {
    const decrement = convertToMXN(500, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
    const minPrice = convertToMXN(200000, 'MXN', investment.exchangeRate, investment.exchangeRateEUR);
    
    if (investment.certificateBasePrice > minPrice) {
      updateInvestment({ certificateBasePrice: investment.certificateBasePrice - decrement });
    }
  };

  const handleIncrementYears = () => {
    if (investment.years < 30) {
      updateInvestment({ years: investment.years + 1 });
    }
  };

  const handleDecrementYears = () => {
    if (investment.years > 5) {
      updateInvestment({ years: investment.years - 1 });
    }
  };

  const handleIncrementProfit = () => {
    if (investment.annualProfit < 30) {
      updateInvestment({ annualProfit: investment.annualProfit + 1 });
    }
  };

  const handleDecrementProfit = () => {
    if (investment.annualProfit > 10) {
      updateInvestment({ annualProfit: investment.annualProfit - 1 });
    }
  };

  // Calculate effective price with 25% increase
  const effectiveCertificatePrice = convertFromMXN(investment.certificateBasePrice * 1.25, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Target className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Datos Centrales - Largo Plazo</h2>
              <p className="text-blue-100">Configura los parámetros de tu inversión a largo plazo con Cosecha Capital</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 space-y-8">
          {/* Initial Certificates */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Certificados Iniciales</h3>
            </div>
            
            <div className="flex items-center justify-center gap-4 mb-5">
              <button 
                onClick={handleDecrementCertificates}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.initialCertificates <= 1}
              >
                <Minus className="w-5 h-5 text-gray-700" />
              </button>
              <div className="text-center">
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={investment.initialCertificates}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 1 && value <= 20) {
                      updateInvestment({ initialCertificates: value });
                    }
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-20 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                />
                <div className="text-sm text-blue-600">
                  certificados
                </div>
              </div>
              <button 
                onClick={handleIncrementCertificates}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.initialCertificates >= 20}
              >
                <Plus className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            <div className="space-y-3">
              <input
                type="range"
                min="1"
                max="20"
                step="1"
                value={investment.initialCertificates}
                onChange={(e) => updateInvestment({ initialCertificates: parseInt(e.target.value) })}
                className="w-full h-2 bg-gradient-to-r from-blue-200 to-blue-300 rounded-lg appearance-none cursor-pointer"
                title="Ajusta el número de certificados iniciales"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>1</span>
                <span>20</span>
              </div>
            </div>

            <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl">
              <div className="text-xs text-gray-600 mb-1">Inversión inicial (precio con incremento del 25%):</div>
              <div className="text-xl font-bold text-gray-900">
                {formatCurrency(investment.initialCertificates * effectiveCertificatePrice, investment.currencyFormat)}
              </div>
              <div className="text-xs text-blue-600 mt-1">
                Precio base: {formatCurrency(convertFromMXN(investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)} + 25% = {formatCurrency(effectiveCertificatePrice, investment.currencyFormat)}
              </div>
            </div>
          </div>

          {/* Certificate Price */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Precio Base del Certificado</h3>
            </div>
            
            <div className="flex items-center justify-center gap-4 mb-5">
              <button 
                onClick={handleDecrementPrice}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.certificateBasePrice <= 200000}
              >
                <Minus className="w-5 h-5 text-gray-700" />
              </button>
              <div className="text-center">
                <input
                  type="number"
                  min={Math.round(convertFromMXN(200000, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR))}
                  max={Math.round(convertFromMXN(300000, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR))}
                  step={Math.round(convertFromMXN(500, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR))}
                  value={Math.round(convertFromMXN(investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR))}
                  onChange={(e) => {
                    const displayValue = parseInt(e.target.value);
                    if (!isNaN(displayValue)) {
                      const mxnValue = convertToMXN(displayValue, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
                      const minPrice = 200000;
                      const maxPrice = 300000;
                      if (mxnValue >= minPrice && mxnValue <= maxPrice) {
                        updateInvestment({ certificateBasePrice: mxnValue });
                      }
                    }
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-32 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                />
                <div className="text-sm text-indigo-600">
                  precio base
                </div>
              </div>
              <button 
                onClick={handleIncrementPrice}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.certificateBasePrice >= 300000}
              >
                <Plus className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            <div className="space-y-3">
              <input
                type="range"
                min={convertFromMXN(200000, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                max={convertFromMXN(300000, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                step={convertFromMXN(500, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                value={convertFromMXN(investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                onChange={(e) => {
                  const displayValue = parseFloat(e.target.value);
                  const mxnValue = convertToMXN(displayValue, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
                  updateInvestment({ certificateBasePrice: mxnValue });
                }}
                className="w-full h-2 bg-gradient-to-r from-indigo-200 to-indigo-300 rounded-lg appearance-none cursor-pointer"
                title="Ajusta el precio base del certificado"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>{formatCurrency(convertFromMXN(200000, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</span>
                <span>{formatCurrency(convertFromMXN(300000, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</span>
              </div>
            </div>
            
            <div className="p-4 bg-indigo-50 rounded-lg">
              <h4 className="font-medium text-indigo-800 mb-2">Características Especiales</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-indigo-700">
                <li>Precio con incremento del 25% sobre el certificado estándar</li>
                <li>Inicio de producción en el mes 61 (año 6) en lugar del mes 49 (año 5)</li>
                <li>La utilidad del primer año de producción es para ti</li>
                <li>Mayor potencial de crecimiento a largo plazo</li>
              </ul>
            </div>
          </div>

          {/* Investment Horizon */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Horizonte de Inversión</h3>
            </div>
            
            <div className="flex items-center justify-center gap-4 mb-5">
              <button 
                onClick={handleDecrementYears}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.years <= 5}
              >
                <Minus className="w-5 h-5 text-gray-700" />
              </button>
              <div className="text-center">
                <input
                  type="number"
                  min="5"
                  max="30"
                  value={investment.years}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 5 && value <= 30) {
                      updateInvestment({ years: value });
                    }
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-20 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                />
                <div className="text-sm text-purple-600">
                  años
                </div>
              </div>
              <button 
                onClick={handleIncrementYears}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.years >= 30}
              >
                <Plus className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              {[10, 15, 20, 25, 30].map((years) => (
                <button
                  key={years}
                  onClick={() => updateInvestment({ years })}
                  className={`py-3 px-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    investment.years === years
                      ? 'bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-md transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={`Establecer horizonte de inversión a ${years} años`}
                >
                  {years}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              <input
                type="range"
                min="5"
                max="30"
                step="1"
                value={investment.years}
                onChange={(e) => updateInvestment({ years: parseInt(e.target.value) })}
                className="w-full h-2 bg-gradient-to-r from-purple-200 to-purple-300 rounded-lg appearance-none cursor-pointer"
                title="Ajusta el horizonte de inversión"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>5 años</span>
                <span>30 años</span>
              </div>
            </div>
          </div>

          {/* Annual Profit */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Parámetros de Producción</h3>
            </div>
            
            {/* Production per Hectare */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">Producción Promedio por Hectárea</label>
              <div className="flex items-center justify-center gap-4 mb-3">
                <button 
                  onClick={() => updateInvestment({ averageProductionPerHectare: Math.max(15000, investment.averageProductionPerHectare - 500) })}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  disabled={investment.averageProductionPerHectare <= 15000}
                >
                  <Minus className="w-5 h-5 text-gray-700" />
                </button>
                <div className="text-center">
                  <input
                    type="number"
                    min="15000"
                    max="35000"
                    step="500"
                    value={investment.averageProductionPerHectare}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value >= 15000 && value <= 35000) {
                        updateInvestment({ averageProductionPerHectare: value });
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="w-24 text-center text-2xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                  />
                  <div className="text-sm text-green-600">
                    kg/hectárea
                  </div>
                </div>
                <button 
                  onClick={() => updateInvestment({ averageProductionPerHectare: Math.min(35000, investment.averageProductionPerHectare + 500) })}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  disabled={investment.averageProductionPerHectare >= 35000}
                >
                  <Plus className="w-5 h-5 text-gray-700" />
                </button>
              </div>
              
              <input
                type="range"
                min="15000"
                max="35000"
                step="500"
                value={investment.averageProductionPerHectare}
                onChange={(e) => updateInvestment({ averageProductionPerHectare: parseInt(e.target.value) })}
                className="w-full h-2 bg-gradient-to-r from-green-200 to-green-300 rounded-lg appearance-none cursor-pointer"
                title="Ajusta la producción promedio por hectárea"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>15,000 kg</span>
                <span>35,000 kg</span>
              </div>
            </div>

            {/* Price per Kg */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">Precio Promedio de Venta por Kg</label>
              <div className="flex items-center justify-center gap-4 mb-3">
                <button 
                  onClick={() => updateInvestment({ averageSalePricePerKg: Math.max(20, investment.averageSalePricePerKg - 1) })}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  disabled={investment.averageSalePricePerKg <= 20}
                >
                  <Minus className="w-5 h-5 text-gray-700" />
                </button>
                <div className="text-center">
                  <input
                    type="number"
                    min="20"
                    max="50"
                    step="1"
                    value={investment.averageSalePricePerKg}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value >= 20 && value <= 50) {
                        updateInvestment({ averageSalePricePerKg: value });
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="w-20 text-center text-2xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                  />
                  <div className="text-sm text-blue-600">
                    MXN/kg
                  </div>
                </div>
                <button 
                  onClick={() => updateInvestment({ averageSalePricePerKg: Math.min(50, investment.averageSalePricePerKg + 1) })}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  disabled={investment.averageSalePricePerKg >= 50}
                >
                  <Plus className="w-5 h-5 text-gray-700" />
                </button>
              </div>
              
              <input
                type="range"
                min="20"
                max="50"
                step="1"
                value={investment.averageSalePricePerKg}
                onChange={(e) => updateInvestment({ averageSalePricePerKg: parseInt(e.target.value) })}
                className="w-full h-2 bg-gradient-to-r from-blue-200 to-blue-300 rounded-lg appearance-none cursor-pointer"
                title="Ajusta el precio promedio de venta por kg"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>$20 MXN</span>
                <span>$50 MXN</span>
              </div>
            </div>

            {/* Calculated Annual Profit Display */}
            <div className="mt-4 p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl">
              <div className="text-xs text-gray-600 mb-1">Utilidad anual calculada:</div>
              <div className="text-xl font-bold text-gray-900">
                {((investment.averageProductionPerHectare * investment.averageSalePricePerKg * 0.1 * 0.65 / investment.certificateBasePrice) * 100).toFixed(1)}%
              </div>
              <div className="text-xs text-green-600 mt-1">
                Basado en {investment.averageProductionPerHectare.toLocaleString()} kg/ha × ${investment.averageSalePricePerKg}/kg × 0.1 × 0.65
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              {[
                { label: 'Conservador', production: 25000, price: 30 },
                { label: 'Moderado', production: 30000, price: 35 },
                { label: 'Optimista', production: 35000, price: 40 }
              ].map((scenario) => (
                <button
                  key={scenario.label}
                  onClick={() => updateInvestment({ 
                    averageProductionPerHectare: scenario.production,
                    averageSalePricePerKg: scenario.price
                  })}
                  className={`py-2 px-3 rounded-xl text-sm font-medium transition-colors ${
                    investment.averageProductionPerHectare === scenario.production && investment.averageSalePricePerKg === scenario.price
                      ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={`Establecer escenario ${scenario.label.toLowerCase()}`}
                >
                  {scenario.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Continue Button */}
          <div className="flex justify-end">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onNext}
              className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <span>Continuar</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default LongTermCentralData;